import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Big Projects · Escrow Rules — admin settings for Large Projects payment mode.
//
// A project's payment mode = f(amount, category):
//   - Amount threshold (default $1000): at/above it -> MILESTONE escrow;
//     below it -> FULL (paid in full up front, escrow-lite).
//   - Per-category overrides (the 7 LP category ENUM keys) can force a mode:
//       FULL      -> always escrow-lite (paid in full)
//       MILESTONE -> always multi-milestone escrow
//       THRESHOLD -> default; fall back to the amount threshold above
//
// Stored on besthand-api as `escrowThresholdUsd` (Float) and
// `escrowCategoryOverridesJson` (a JSON map, e.g. {"INSTALLATION":"FULL"}).
//
// ALL operations run against besthand-api through the `/lp-graphql` proxy via
// the `lpApi` helper — NOT the web's own /graphql schema (no LP resolver is
// duplicated in besthand-web, per the architecture decision). Admin auth is
// bridged through the proxy as `authToken`; besthand-api's `requireAdmin`
// gates the updateEscrowSettings mutation.
// ============================================================================

const Q_ESCROW = `
  query {
    escrowSettings {
      status errorMessage escrowThresholdUsd escrowCategoryOverridesJson
    }
  }
`;

const M_UPDATE = `
  mutation ($thresholdUsd: Float, $categoryOverridesJson: String) {
    updateEscrowSettings(thresholdUsd: $thresholdUsd, categoryOverridesJson: $categoryOverridesJson) {
      status errorMessage escrowThresholdUsd escrowCategoryOverridesJson
    }
  }
`;

// The 7 LP category ENUM keys, in display order.
const CATEGORIES = [
  { key: 'KITCHEN', label: 'Kitchen' },
  { key: 'BATHROOM', label: 'Bathroom' },
  { key: 'BASEMENT', label: 'Basement' },
  { key: 'EXTERIOR', label: 'Exterior' },
  { key: 'ROOFING', label: 'Roofing' },
  { key: 'INSTALLATION', label: 'Installation' },
  { key: 'OTHER', label: 'Other' },
];

// Allowed override modes per category.
const MODES = [
  { value: 'THRESHOLD', label: 'Use threshold (default)' },
  { value: 'FULL', label: 'Always full (escrow-lite)' },
  { value: 'MILESTONE', label: 'Always milestone escrow' },
];

const DEFAULT_THRESHOLD = '1000';

// --- styles (inline, mirrors VisitFee.js / lightweight admin pages) ---
const S = {
  page: { maxWidth: 720, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20, lineHeight: 1.5 },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '22px 24px', marginBottom: 16, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  cardTitle: { fontSize: 16, fontWeight: 800, margin: '0 0 4px' },
  cardSub: { color: '#6b7280', fontSize: 13, marginTop: 0, marginBottom: 16, lineHeight: 1.5 },
  amountLabel: { display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 },
  inputRow: { display: 'flex', alignItems: 'center', gap: 0, maxWidth: 220 },
  dollar: { padding: '11px 12px', background: '#f6f8f9', border: '1px solid #d8dde2', borderRight: 'none', borderRadius: '9px 0 0 9px', fontSize: 15, fontWeight: 700, color: '#6b7280' },
  input: { flex: 1, padding: '10px 12px', border: '1px solid #d8dde2', borderRadius: '0 9px 9px 0', fontSize: 15, fontWeight: 600, outline: 'none', width: '100%', minWidth: 0 },
  inputHint: { color: '#9ca3af', fontSize: 12, marginTop: 6 },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 4 },
  th: { textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, padding: '0 0 10px', borderBottom: '1px solid #eef1f3' },
  td: { padding: '12px 0', borderBottom: '1px solid #f3f5f6', verticalAlign: 'middle' },
  catName: { fontSize: 15, fontWeight: 700, color: '#0f1214' },
  select: { padding: '9px 12px', border: '1px solid #d8dde2', borderRadius: 9, fontSize: 14, fontWeight: 600, outline: 'none', background: '#fff', minWidth: 230, cursor: 'pointer' },
  actions: { display: 'flex', gap: 10, marginTop: 22 },
  btn: { background: '#e8232b', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 22px', fontSize: 14, fontWeight: 800, cursor: 'pointer' },
  btnDisabled: { background: '#f0bcbe', cursor: 'not-allowed' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
};

// Build a fresh overrides map (every category -> THRESHOLD).
function emptyOverrides() {
  const o = {};
  CATEGORIES.forEach((c) => { o[c.key] = 'THRESHOLD'; });
  return o;
}

// Parse the stored JSON map into a full {KEY: MODE} object, defaulting any
// missing/invalid key to THRESHOLD.
function parseOverrides(json) {
  const out = emptyOverrides();
  if (!json) return out;
  let parsed;
  try {
    parsed = JSON.parse(json);
  } catch (e) {
    return out;
  }
  if (!parsed || typeof parsed !== 'object') return out;
  CATEGORIES.forEach((c) => {
    const v = parsed[c.key];
    if (v === 'FULL' || v === 'MILESTONE' || v === 'THRESHOLD') {
      out[c.key] = v;
    }
  });
  return out;
}

function EscrowRules() {
  const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD);
  const [overrides, setOverrides] = useState(emptyOverrides());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_ESCROW);
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.escrowSettings;
        if (env && env.status === 200) {
          setThreshold(env.escrowThresholdUsd != null ? String(env.escrowThresholdUsd) : DEFAULT_THRESHOLD);
          setOverrides(parseOverrides(env.escrowCategoryOverridesJson));
        } else if (env) {
          setMsg({ error: env.errorMessage || `Error ${env.status}` });
        }
      }
    } catch (e) {
      setMsg({ error: `Could not load escrow rules: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setCategory = (key, value) => {
    setOverrides((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setMsg(null);
    const amt = Number(threshold);
    if (Number.isNaN(amt) || amt < 0 || amt > 1000000) {
      setMsg({ error: 'Threshold must be a number between $0 and $1,000,000.' });
      return;
    }
    // Build the JSON map. We store all 7 keys (storing THRESHOLD entries is fine).
    const categoryOverridesJson = JSON.stringify(overrides);
    setSaving(true);
    try {
      const res = await lpApi(M_UPDATE, {
        thresholdUsd: Math.round(amt * 100) / 100,
        categoryOverridesJson,
      });
      if (res.errors) { setMsg({ error: firstError(res) }); setSaving(false); return; }
      const env = res.data && res.data.updateEscrowSettings;
      if (env && env.status === 200) {
        setThreshold(env.escrowThresholdUsd != null ? String(env.escrowThresholdUsd) : threshold);
        setOverrides(parseOverrides(env.escrowCategoryOverridesJson));
        setMsg({ ok: 'Escrow rules saved.' });
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Save failed: ${e.message}` });
    }
    setSaving(false);
  };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Escrow Rules</h2>
      <div style={S.hint}>
        Large Projects — how a published project is paid. Below the threshold a project is
        paid in full up front (escrow-lite); at or above the threshold it is split into
        milestone escrow. Per-category overrides can force a fixed mode regardless of amount.
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}

      {loading ? (
        <div style={S.empty}>Loading…</div>
      ) : (
        <React.Fragment>
          <div style={S.card}>
            <p style={S.cardTitle}>Amount threshold</p>
            <p style={S.cardSub}>
              Below this amount = paid in full (escrow-lite); at or above = milestone escrow.
            </p>
            <label style={S.amountLabel} htmlFor="escrow-threshold">Threshold (USD)</label>
            <div style={S.inputRow}>
              <span style={S.dollar}>$</span>
              <input
                id="escrow-threshold"
                style={S.input}
                type="number"
                min="0"
                max="1000000"
                step="1"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
              />
            </div>
            <div style={S.inputHint}>Default $1,000. Applies to any category set to “Use threshold”.</div>
          </div>

          <div style={S.card}>
            <p style={S.cardTitle}>Category overrides</p>
            <p style={S.cardSub}>
              Force a payment mode per category, or leave on “Use threshold” to fall back to the
              amount rule above.
            </p>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Category</th>
                  <th style={S.th}>Payment mode</th>
                </tr>
              </thead>
              <tbody>
                {CATEGORIES.map((c) => (
                  <tr key={c.key}>
                    <td style={S.td}><span style={S.catName}>{c.label}</span></td>
                    <td style={S.td}>
                      <select
                        style={S.select}
                        value={overrides[c.key] || 'THRESHOLD'}
                        onChange={(e) => setCategory(c.key, e.target.value)}
                        aria-label={`Payment mode for ${c.label}`}
                      >
                        {MODES.map((m) => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={S.actions}>
            <button
              type="button"
              style={saving ? { ...S.btn, ...S.btnDisabled } : S.btn}
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </React.Fragment>
      )}
    </div>
  );
}

export default EscrowRules;
